/**
 * One added Claude login in Settings → Logins: its sign-in, the account it
 * is signed in to, and a warning when another login uses the same account.
 */
import { Button, Icons, PocketRow, type PocketRowProps } from "@/components/ui";
import type { CliLogin } from "@/desktop/cli-logins";
import { useT } from "@/i18n";
import { settingsCliLoginsMessages } from "@/i18n/messages/settings-cli-logins";
import type { CliLoginSignIn } from "./use-cli-login-sign-in";

export function CliLoginRow({
  login,
  signedIn,
  account,
  sameAccountAs,
  run,
  signInBusy,
  removing,
  onSignIn,
  onStop,
  onUseBrowser,
  onRename,
  onRemove,
}: {
  login: CliLogin;
  /** `undefined` while the check runs; `null` when it could not be read. */
  signedIn: boolean | null | undefined;
  /** "a@b.com · Max" once signed in. */
  account: string | null;
  /** The other login signed in to the same account. */
  sameAccountAs: string | null;
  run: CliLoginSignIn | null;
  signInBusy: boolean;
  removing: boolean;
  onSignIn: () => void;
  onStop: () => void;
  onUseBrowser: () => void;
  onRename: () => void;
  onRemove: () => void;
}) {
  const t = useT(settingsCliLoginsMessages);
  const running = run !== null && run.phase !== "failed";
  const inWindow = running && run.signInPage && !run.inBrowser;
  const secondLine = running
    ? run.phase === "running"
      ? t("signingIn")
      : inWindow
        ? t("finishInWindow")
        : t("finishInBrowser")
    : run?.phase === "failed"
      ? t("signInFailed")
      : signedIn === undefined
        ? t("checking")
        : signedIn === null
          ? undefined
          : signedIn
            ? account
              ? t("signedInAs", { account })
              : t("signedIn")
            : t("notSignedIn");
  const status: PocketRowProps["status"] = running
    ? { tone: "working" }
    : run?.phase === "failed"
      ? { tone: "error" }
      : signedIn === false
        ? { tone: "question" }
        : undefined;
  const offerSignIn =
    run?.phase === "failed" ||
    signedIn === false ||
    signedIn === null ||
    (signedIn === true && sameAccountAs !== null);
  return (
    <PocketRow
      testId={`cli-login-${login.id}`}
      leading={<Icons.User className="h-4 w-4 text-muted" />}
      title={login.name}
      secondLine={secondLine}
      status={status}
      trailing={
        running ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={onStop}
            data-testid={`cli-login-stop-${login.id}`}
          >
            {t("stop")}
          </Button>
        ) : offerSignIn ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={signInBusy || removing}
            onClick={onSignIn}
            data-testid={`cli-login-sign-in-${login.id}`}
          >
            {t("signIn")}
          </Button>
        ) : undefined
      }
      below={
        inWindow ? (
          <div className="flex flex-col items-start gap-1 pt-1">
            <p
              className="text-appkit-small text-muted"
              data-testid={`cli-login-browser-hint-${login.id}`}
            >
              {t("browserHint")}
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={onUseBrowser}
              data-testid={`cli-login-use-browser-${login.id}`}
            >
              {t("useBrowserInstead")}
            </Button>
          </div>
        ) : !running && signedIn === true && sameAccountAs !== null ? (
          <p
            className="flex items-start gap-1.5 pt-1 text-appkit-mini text-warning"
            data-testid={`cli-login-same-account-${login.id}`}
          >
            <Icons.AlertTriangle className="mt-px h-3 w-3 shrink-0" />
            {t("sameAccount", { name: sameAccountAs })}
          </p>
        ) : undefined
      }
      hoverActions={
        <>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRename}
            data-testid={`cli-login-rename-${login.id}`}
          >
            {t("rename")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={removing}
            onClick={onRemove}
            data-testid={`cli-login-remove-${login.id}`}
          >
            {t("remove")}
          </Button>
        </>
      }
    />
  );
}
